import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import WorkspaceSidebar from '../../components/ui/WorkspaceSidebar';
import UserProfileDropdown from '../../components/ui/UserProfileDropdown';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterControls from './components/FilterControls';
import KanbanColumn from './components/KanbanColumn';
import RequirementDetailPanel from './components/RequirementDetailPanel';
import AIAnalysisPanel from './components/AIAnalysisPanel';

const FitGapBoard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [draggingCard, setDraggingCard] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    complexity: 'all',
    lob: 'all',
    assignee: 'all'
  });

  // Mock requirements data
  const [requirements, setRequirements] = useState([
    {
      id: 'req-001',
      title: 'Custom Approval Workflow for Purchase Orders',
      description: 'Implement multi-level approval process with dynamic routing based on amount and category',
      type: 'functional',
      lob: 'P2P',
      complexity: 'High',
      status: 'Proposed',
      assignee: 'Sarah Chen',
      sapFitPercentage: 45,
      effortDays: 18,
      costEstimate: '$45,000',
      hasRicefw: true,
      fitGapReasoning: 'SAP standard approval workflow does not support the complex routing logic required by business rules. Custom development needed for approval matrix and escalation paths.',
      recommendedAction: 'Develop custom workflow using SAP BTP Workflow Management',
      ricefwItems: [
        { type: 'Workflow', complexity: 'High' },
        { type: 'Enhancement', complexity: 'Medium' }
      ],
      activities: [
        { action: 'Requirement created from questionnaire', user: 'System', timestamp: '2 hours ago' },
        { action: 'Assigned to Sarah Chen', user: 'Michael Rodriguez', timestamp: '1 hour ago' }
      ]
    },
    {
      id: 'req-002',
      title: 'Vendor Master Data Synchronization',
      description: 'Real-time sync of vendor information between SAP and external procurement system',
      type: 'integration',
      lob: 'P2P',
      complexity: 'Medium',
      status: 'Analyzing',
      assignee: 'David Kim',
      sapFitPercentage: 75,
      effortDays: 12,
      costEstimate: '$28,000',
      hasRicefw: true,
      fitGapReasoning: 'SAP provides standard integration capabilities through APIs, but custom mapping and transformation logic required for legacy system compatibility.',
      recommendedAction: 'Use SAP Integration Suite with custom transformation logic',
      ricefwItems: [
        { type: 'Interface', complexity: 'Medium' },
        { type: 'Conversion', complexity: 'Low' }
      ],
      activities: [
        { action: 'Analysis started', user: 'David Kim', timestamp: '3 hours ago' },
        { action: 'Technical review completed', user: 'Emma Thompson', timestamp: '1 hour ago' }
      ]
    },
    {
      id: 'req-003',
      title: 'Standard Three-Way Matching Process',
      description: 'Implement standard SAP three-way matching for purchase orders, goods receipts, and invoices',
      type: 'functional',
      lob: 'P2P',
      complexity: 'Low',
      status: 'Fit',
      assignee: 'Michael Rodriguez',
      sapFitPercentage: 95,
      effortDays: 3,
      costEstimate: '$7,500',
      hasRicefw: false,
      fitGapReasoning: 'Standard SAP functionality fully supports the three-way matching process with minimal configuration required.',
      recommendedAction: 'Configure standard SAP three-way matching with tolerance settings',
      activities: [
        { action: 'Fit analysis completed', user: 'Michael Rodriguez', timestamp: '4 hours ago' },
        { action: 'Configuration approach documented', user: 'Sarah Chen', timestamp: '2 hours ago' }
      ]
    },
    {
      id: 'req-004',
      title: 'Custom Financial Reporting Dashboard',
      description: 'Executive dashboard with real-time P&L, cash flow, and KPI visualization',
      type: 'reporting',
      lob: 'RTR',
      complexity: 'High',
      status: 'Gap',
      assignee: 'Emma Thompson',
      sapFitPercentage: 35,
      effortDays: 25,
      costEstimate: '$62,000',
      hasRicefw: true,
      fitGapReasoning: 'SAP Analytics Cloud provides basic reporting capabilities, but the specific executive dashboard requirements and real-time data integration exceed standard functionality.',
      recommendedAction: 'Develop custom dashboard using SAP Analytics Cloud with embedded analytics',
      ricefwItems: [
        { type: 'Report', complexity: 'High' },
        { type: 'Interface', complexity: 'Medium' },
        { type: 'Enhancement', complexity: 'High' }
      ],
      activities: [
        { action: 'Gap identified during analysis', user: 'Emma Thompson', timestamp: '6 hours ago' },
        { action: 'RICEFW items documented', user: 'David Kim', timestamp: '4 hours ago' }
      ]
    },
    {
      id: 'req-005',
      title: 'Standard Order-to-Cash Process',
      description: 'Implement standard SAP sales order processing with delivery and billing',
      type: 'functional',
      lob: 'OTC',
      complexity: 'Low',
      status: 'Approved',
      assignee: 'Sarah Chen',
      sapFitPercentage: 98,
      effortDays: 5,
      costEstimate: '$12,000',
      hasRicefw: false,
      fitGapReasoning: 'Standard SAP Order-to-Cash process fully meets business requirements with standard configuration.',
      recommendedAction: 'Configure standard SAP SD module with pricing procedures',
      activities: [
        { action: 'Requirement approved by stakeholders', user: 'Michael Rodriguez', timestamp: '1 day ago' },
        { action: 'Configuration document created', user: 'Sarah Chen', timestamp: '8 hours ago' }
      ]
    },
    {
      id: 'req-006',
      title: 'Inventory Valuation Enhancement',
      description: 'Custom logic for inventory valuation based on market conditions and aging',
      type: 'technical',
      lob: 'Plan',
      complexity: 'Medium',
      status: 'Proposed',
      assignee: null,
      sapFitPercentage: 60,
      effortDays: 15,
      costEstimate: '$38,000',
      hasRicefw: true,
      fitGapReasoning: 'SAP provides standard valuation methods, but custom aging and market condition logic requires enhancement to standard functionality.',
      recommendedAction: 'Enhance standard valuation with custom user exit',
      ricefwItems: [
        { type: 'Enhancement', complexity: 'Medium' },
        { type: 'Report', complexity: 'Low' }
      ],
      activities: [
        { action: 'Requirement created', user: 'System', timestamp: '30 minutes ago' }
      ]
    }
  ]);

  const kanbanColumns = ['Proposed', 'Analyzing', 'Fit', 'Gap', 'Approved'];

  // Filter requirements based on current filters
  const filteredRequirements = requirements?.filter(req => {
    if (filters?.search && !req?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !req?.description?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
      return false;
    }
    if (filters?.type !== 'all' && req?.type !== filters?.type) return false;
    if (filters?.complexity !== 'all' && req?.complexity !== filters?.complexity) return false;
    if (filters?.lob !== 'all' && req?.lob !== filters?.lob) return false;
    if (filters?.assignee !== 'all') {
      if (filters?.assignee === 'unassigned' && req?.assignee) return false;
      if (filters?.assignee !== 'unassigned' && req?.assignee !== filters?.assignee) return false;
    }
    return true;
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      type: 'all',
      complexity: 'all',
      lob: 'all',
      assignee: 'all'
    });
  };

  const handleBulkAction = (action) => {
    console.log('Bulk action:', action);
    // Implement bulk actions like move, assign, export
  };

  const handleCardSelect = (requirement) => {
    setSelectedRequirement(requirement);
    setShowDetailPanel(true);
  };

  const handleCardDrop = (cardId, newStatus) => {
    setRequirements(prev => prev?.map(req => 
      req?.id === cardId ? { ...req, status: newStatus } : req
    ));
    setDragOverColumn(null);
  };

  const handleDragStart = (requirement) => {
    setDraggingCard(requirement);
  };

  const handleDragEnd = () => {
    setDraggingCard(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (columnStatus) => {
    setDragOverColumn(columnStatus);
  };

  const handleRequirementUpdate = (updatedRequirement) => {
    setRequirements(prev => prev?.map(req => 
      req?.id === updatedRequirement?.id ? updatedRequirement : req
    ));
    setSelectedRequirement(updatedRequirement);
  };

  const getColumnStats = () => {
    const stats = {};
    kanbanColumns?.forEach(column => {
      stats[column] = filteredRequirements?.filter(req => req?.status === column)?.length;
    });
    return stats;
  };

  const stats = getColumnStats();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Fit/Gap Analysis Board - BPMLflows</title>
        <meta name="description" content="Kanban-style requirements analysis with automated SAP Best Practice comparison and AI-powered gap analysis." />
      </Helmet>
      <div className="flex h-screen">
        {/* Sidebar */}
        <WorkspaceSidebar 
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        {/* Main Content */}
        <div className={`flex-1 flex flex-col nav-transition ${
          sidebarCollapsed ? 'ml-16' : 'ml-64'
        } md:ml-0`}>
          {/* Header */}
          <header className="bg-card border-b border-border px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div>
                  <h1 className="text-2xl font-bold text-foreground font-inter">
                    Fit/Gap Analysis Board
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Kanban-style requirements analysis with SAP Best Practice comparison
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <Button
                  variant="outline"
                  iconName="Bot"
                  onClick={() => setShowAIPanel(!showAIPanel)}
                >
                  AI Assistant
                </Button>
                
                <Button
                  variant="outline"
                  iconName="Download"
                  onClick={() => handleBulkAction('export-all')}
                >
                  Export Board
                </Button>

                <UserProfileDropdown />
              </div>
            </div>
          </header>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-6">
              <BreadcrumbNavigation />

              {/* Stats Overview */}
              <div className="grid grid-cols-5 gap-4 mb-6">
                {kanbanColumns?.map(column => (
                  <div key={column} className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{column}</p>
                        <p className="text-2xl font-bold text-foreground">{stats?.[column]}</p>
                      </div>
                      <Icon 
                        name={
                          column === 'Proposed' ? 'FileText' :
                          column === 'Analyzing' ? 'Search' :
                          column === 'Fit' ? 'CheckCircle2' :
                          column === 'Gap' ? 'AlertCircle' : 'CheckCircle'
                        } 
                        size={20} 
                        className="text-muted-foreground"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Filter Controls */}
              <FilterControls
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onBulkAction={handleBulkAction}
                selectedCount={0}
              />
            </div>

            {/* Kanban Board */}
            <div className="flex-1 flex overflow-hidden">
              <div className="flex-1 flex overflow-x-auto">
                <div className="flex space-x-4 p-6 pt-0 min-w-max">
                  {kanbanColumns?.map(column => (
                    <div 
                      key={column} 
                      className={`w-80 bg-card border border-border rounded-lg overflow-hidden ${
                        dragOverColumn === column ? 'ring-2 ring-primary border-primary' : ''
                      }`}
                    >
                      <KanbanColumn
                        status={column}
                        requirements={filteredRequirements}
                        onCardSelect={handleCardSelect}
                        selectedCard={selectedRequirement}
                        onDrop={handleCardDrop}
                        onDragOver={handleDragOver}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        draggingCard={draggingCard}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Detail Panel */}
              {showDetailPanel && (
                <RequirementDetailPanel
                  requirement={selectedRequirement}
                  onClose={() => setShowDetailPanel(false)}
                  onUpdate={handleRequirementUpdate}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* AI Analysis Panel */}
      <AIAnalysisPanel
        isVisible={showAIPanel}
        onToggle={() => setShowAIPanel(!showAIPanel)}
        selectedRequirement={selectedRequirement}
      />
      {/* Mobile Bottom Navigation Spacer */}
      <div className="h-16 md:hidden" />
    </div>
  );
};

export default FitGapBoard;